/**
 * components/landing/home/journey/data.js — Phase 2.3 · single source of
 * truth for the Customer Success Journey.
 *
 * Each step is a discrete milestone of the White-Label onboarding
 * lifecycle.  The section renderer consumes this list to draw both the
 * top progress indicator and the active-step card.  Adding a step
 * (e.g. "Compliance certification") = one entry here.
 *
 * ── Step schema ──
 *   id              stable slug (deep-linkable: #journey-<id>)
 *   num             1-indexed step number
 *   title           card headline
 *   short           one-word label used in the top progress strip
 *   message         business-message quote (from PRD)
 *   summary         short paragraph shown right under the headline
 *   iconKey         lucide-react icon name (resolved in illustrations.jsx)
 *   tone            'orange' | 'green' | 'blue' | 'purple' | 'cyan'
 *   features        4-6 short bullet points (what the customer receives)
 *   estimatedTime   human-readable time estimate ("~2 minutes", "1 day")
 *   illustration    key into ILLUSTRATIONS registry (illustrations.jsx)
 *   ctaLabel/ctaHref (optional — some steps carry a nudge CTA)
 */

export const JOURNEY_STEPS = [
  {
    id: 'plan',
    num: 1,
    title: 'Choose your subscription',
    short: 'Plan',
    message: 'Start with the subscription that matches your growth stage.',
    summary: 'Compare three transparent plans — Quarterly, Half-Year, or Annual — and pick the one that fits where your business is heading next.',
    iconKey: 'Wallet',
    tone: 'orange',
    features: [
      'Instant plan comparison',
      'No credit card lock-in',
      'Downgrade or upgrade any time',
      'Includes all core modules',
    ],
    estimatedTime: '~2 minutes',
    illustration: 'plan',
    ctaLabel: 'View pricing',
    ctaHref: '/pricing',
  },
  {
    id: 'checkout',
    num: 2,
    title: 'Secure Stripe checkout',
    short: 'Checkout',
    message: 'Payments are securely processed and your license is activated immediately after successful checkout.',
    summary: 'PCI-DSS compliant Stripe checkout with instant invoice, tax handling for 30+ countries, and licence activation within seconds of the payment settling.',
    iconKey: 'CreditCard',
    tone: 'green',
    features: [
      'PCI-DSS compliant Stripe',
      'Instant tax invoice',
      '3D-secure + strong customer auth',
      'Licence keys emailed & in-app',
    ],
    estimatedTime: '~3 minutes',
    illustration: 'checkout',
  },
  {
    id: 'provisioning',
    num: 3,
    title: 'Workspace provisioning',
    short: 'Provision',
    message: 'Your dedicated Kalki Ai Cloud workspace is prepared automatically.',
    summary: 'The moment your payment clears, our orchestrator spins up a dedicated tenant workspace, generates admin credentials, applies your license entitlements and warms up the AI Mentor knowledge base.',
    iconKey: 'ServerCog',
    tone: 'blue',
    features: [
      'Dedicated tenant + database',
      'Auto-generated admin account',
      'Region-scoped deployment',
      'Encrypted credentials handoff',
    ],
    estimatedTime: '~90 seconds',
    illustration: 'provisioning',
  },
  {
    id: 'branding',
    num: 4,
    title: 'Brand identity setup',
    short: 'Branding',
    message: 'Transform the platform into your own brand.',
    summary: 'Upload your logo, pick brand colors, set your favicon and email sender identity. The whole platform — dashboards, receipts, transactional emails — instantly reflects your brand.',
    iconKey: 'Palette',
    tone: 'purple',
    features: [
      'Logo + wordmark uploads',
      'Primary / accent / success colors',
      'Favicon + PWA icons',
      'Branded transactional emails',
      'Auto-generated dark & light themes',
    ],
    estimatedTime: '~15 minutes',
    illustration: 'branding',
  },
  {
    id: 'domain',
    num: 5,
    title: 'Custom domain configuration',
    short: 'Domain',
    message: 'Launch using your own business domain with enterprise-grade security.',
    summary: 'Connect your domain in a single form. We verify the DNS, provision a free Let\u2019s Encrypt SSL certificate, and validate the platform URL — all fully automated.',
    iconKey: 'Globe',
    tone: 'cyan',
    features: [
      'DNS auto-validation',
      'Free auto-renewing SSL',
      'CDN + edge caching',
      'HSTS + strict TLS by default',
    ],
    estimatedTime: '~10 minutes (DNS propagation dependent)',
    illustration: 'domain',
  },
  {
    id: 'modules',
    num: 6,
    title: 'Platform module configuration',
    short: 'Modules',
    message: 'Activate the capabilities your business needs.',
    summary: 'Toggle modules on and off from a single console. Every module ships pre-integrated — no extra vendors, no glue code, no data warehouse. Add or remove as your business evolves.',
    iconKey: 'ToggleRight',
    tone: 'orange',
    features: [
      'AI Mentor · Trading Terminal',
      'Strategy Builder · Backtesting',
      'Trading Academy · Analytics',
      'Billing · White-Label Settings',
    ],
    estimatedTime: '~20 minutes',
    illustration: 'modules',
  },
  {
    id: 'team',
    num: 7,
    title: 'Team & access management',
    short: 'Team',
    message: 'Collaborate securely with your team from day one.',
    summary: 'Invite operators, analysts, admins and read-only stakeholders. Role-based access control, SSO integrations and audit trails come pre-wired.',
    iconKey: 'UsersRound',
    tone: 'green',
    features: [
      'Role-based access (RBAC)',
      'SSO ready (Okta / Azure / Google)',
      'Fine-grained module permissions',
      'Full audit log · 90-day retention',
    ],
    estimatedTime: '~15 minutes',
    illustration: 'team',
  },
  {
    id: 'launch',
    num: 8,
    title: 'Launch your platform',
    short: 'Launch',
    message: 'Your AI Trading SaaS platform is now live and ready for your customers.',
    summary: 'One click flips your workspace from staging to live. Branding, domain, modules and team are validated end-to-end and your platform URL becomes public.',
    iconKey: 'Rocket',
    tone: 'orange',
    features: [
      'End-to-end validation checklist',
      'One-click go-live',
      'Public status page',
      'Launch announcement templates',
    ],
    estimatedTime: '~5 minutes',
    illustration: 'launch',
  },
  {
    id: 'growth',
    num: 9,
    title: 'Continuous growth & success',
    short: 'Growth',
    message: 'Kalki Ai Cloud continues to evolve with your business.',
    summary: 'You keep the customer relationship — we keep shipping. Weekly product updates, quarterly AI upgrades, dedicated support and health monitoring — all included in every plan.',
    iconKey: 'TrendingUp',
    tone: 'green',
    features: [
      'Weekly product updates',
      'Quarterly AI model refresh',
      'Dedicated launch & CS partner',
      '24/7 monitoring · 99.98% SLA',
      'Customer success playbooks',
    ],
    estimatedTime: 'Ongoing',
    illustration: 'growth',
  },
]

// Tone → colour tokens used across the section (kept in sync with the
// architecture engine so the whole homepage feels consistent).
export const TONE = {
  orange: { hex: '#FFC107', rgb: '243,112,33' },
  green:  { hex: '#22c55e', rgb: '34,197,94' },
  blue:   { hex: '#3b82f6', rgb: '59,130,246' },
  purple: { hex: '#a855f7', rgb: '168,85,247' },
  cyan:   { hex: '#22d3ee', rgb: '34,211,238' },
}

export const stepById = (id) => JOURNEY_STEPS.find((s) => s.id === id)
export const stepByNum = (n) => JOURNEY_STEPS.find((s) => s.num === n)
